<template>
  <div class="card shadow-sm h-100">
    <div class="card-body">
      <div v-if="forceComfort || (!forceComfort && !forceStress)">
        <div class="mb-5">
          <div class="chart-container mb-2" style="height: 300px;">
            <canvas :id="chartIdComfort" :key="chartKeyComfort"></canvas>
          </div>
        </div>
      </div>
      <div v-if="forceStress || (!forceComfort && !forceStress)">
        <div class="mb-5">
          <div class="chart-container mb-2" style="height: 300px;">
            <canvas :id="chartIdStress" :key="chartKeyStress"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import Chart from 'chart.js/auto'

const props = defineProps({
  surveyReport: {
    type: Object,
    required: true
  },
  selectedCategory: {
    type: String,
    required: true
  },
  selectedPopulation: {
    type: [String, Boolean, Number],
    required: true
  },
  forceComfort: {
    type: Boolean,
    default: false
  },
  forceStress: {
    type: Boolean,
    default: false
  }
})

// Remove isGrouped logic, always show chart if data exists
const chartType = ref('bar')
const isComponentMounted = ref(false)
const chartIdComfort = computed(() => `subcat-all-comfort-chart-${props.selectedCategory}`)
const chartIdStress = computed(() => `subcat-all-stress-chart-${props.selectedCategory}`)
const chartKeyComfort = computed(() => `subcat-all-comfort-key-${props.selectedCategory}-${props.selectedPopulation}-${chartType.value}`)
const chartKeyStress = computed(() => `subcat-all-stress-key-${props.selectedCategory}-${props.selectedPopulation}-${chartType.value}`)

const getPopulationKeys = () => {
  const keys = Object.keys(props.surveyReport || {})
  if (props.selectedPopulation === false || props.selectedPopulation === 'false' || props.selectedPopulation === 0 || props.selectedPopulation === '0') {
    if (keys.includes('all_populations') && props.surveyReport?.all_populations?.types?.[props.selectedCategory]) {
      return ['all_populations']
    }
    return []
  }
  return keys.filter(key => key !== 'all_populations' && !!props.surveyReport?.[key]?.types?.[props.selectedCategory])
}

const subcategoryLabels = computed(() => {
  const populationGroups = getPopulationKeys()
  const set = new Set()
  populationGroups.forEach(popId => {
    const subcats = props.surveyReport?.[popId]?.types?.[props.selectedCategory]?.sub_categories
    if (subcats && typeof subcats === 'object') {
      Object.keys(subcats).forEach(n => set.add(n))
    }
  })
  return Array.from(set)
})

const buildDatasets = (statType) => {
  const populationGroups = getPopulationKeys()
  const datasets = []
  subcategoryLabels.value
  populationGroups.forEach(popId => {
    const popData = props.surveyReport[popId]
    const data = subcategoryLabels.value.map(subcat => {
      const subcatData = popData?.types?.[props.selectedCategory]?.sub_categories?.[subcat]
      let percent = 0
      if (subcatData && typeof subcatData[statType] === 'number') {
        percent = subcatData[statType]
      }
      return percent
    })
    datasets.push({
      label: popData?.name || popId,
      acronym: popData?.acronym || popId,
      data,
      backgroundColor: popData?.color || '#36a2eb',
      borderColor: popData?.color || '#36a2eb',
      borderWidth: 1,
    })
  })
  return datasets
}

/*const getChartOptions = (statType) => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label || ''}: ${context.raw}%`;
          }
        }
      },
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 40,
          boxHeight: 8,
          padding: 20,
          color: '#000',
          font: { size: 12 },
        },
        usePointStyle: false,
        pointStyle: 'rectRounded',
      },
      title: {
        display: false
      }
    },
    scales: chartType.value === 'bar' ? {
      y: {
        max:100,
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { drawBorder: false }
      },
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          autoSkipPadding: 20,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            const words = label.split(' ');
            const lines = [];
            let currentLine = '';
            words.forEach(word => {
              if ((currentLine + word).length > 20) {
                lines.push(currentLine.trim());
                currentLine = word + ' ';
              } else {
                currentLine += word + ' ';
              }
            });
            lines.push(currentLine.trim());
            return lines.length > 1 ? lines : label;
          }
        }
      }
    } : undefined,
    datasets: {
      bar: {
        categoryPercentage: 0.35,
        barPercentage: 0.9,
        maxBarThickness: 30
      }
    }
  }
}*/


const getChartOptions = (statType) => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            // même logique appliquée au tooltip
            if (context.dataset.acronym === 'all_populations') {
              return `Tous populations: ${context.raw}%`;
            } else if (context.dataset.acronym) {
              return `${context.dataset.label} (${context.dataset.acronym}): ${context.raw}%`;
            }
            return `${context.dataset.label}: ${context.raw}%`;
          }
        }
      },
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 40,
          boxHeight: 8,
          padding: 20,
          color: '#000',
          font: { size: 12 },
          generateLabels: function (chart) {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => {
              let text;
              if (dataset.acronym === 'all_populations') {
                text = 'Tous populations';
              } else if (dataset.acronym) {
                text = `${dataset.label} (${dataset.acronym})`;
              } else {
                text = dataset.label;
              }

              return {
                text,
                fillStyle: dataset.backgroundColor,
                strokeStyle: dataset.borderColor,
                hidden: !chart.isDatasetVisible(i),
                index: i
              };
            });
          }
        },
        usePointStyle: false,
        pointStyle: 'rectRounded',
      },
      title: {
        display: false
      }
    },
    scales: chartType.value === 'bar' ? {
      y: {
        max: 100,
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { drawBorder: false }
      },
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          autoSkipPadding: 20,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            const words = label.split(' ');
            const lines = [];
            let currentLine = '';
            words.forEach(word => {
              if ((currentLine + word).length > 20) {
                lines.push(currentLine.trim());
                currentLine = word + ' ';
              } else {
                currentLine += word + ' ';
              }
            });
            lines.push(currentLine.trim());
            return lines.length > 1 ? lines : label;
          }
        }
      }
    } : undefined,
    datasets: {
      bar: {
        categoryPercentage: 0.35,
        barPercentage: 0.9,
        maxBarThickness: 30
      }
    }
  }
}

const chartInstances = ref({})

const destroyCharts = () => {
  Object.values(chartInstances.value).forEach(chart => {
    if (chart && typeof chart.destroy === 'function') {
      try {
        chart.destroy()
      } catch (e) {
      }
    }
  })
  chartInstances.value = {}
}

const renderCharts = () => {
  if (!isComponentMounted.value) return

  destroyCharts()

  nextTick(() => {
    if (!isComponentMounted.value) return

    const comfortCanvas = document.getElementById(chartIdComfort.value)
    if (comfortCanvas) {
      try {
        chartInstances.value[chartIdComfort.value] = new Chart(comfortCanvas.getContext('2d'), {
          type: chartType.value,
          data: {
            labels: subcategoryLabels.value,
            datasets: buildDatasets('comfortability_percent')
          },
          options: getChartOptions('comfortability_percent')
        })
      } catch (e) {
        console.error('Error creating comfort chart:', e)
      }
    }
    // Stress chart
    const stressCanvas = document.getElementById(chartIdStress.value)
    if (stressCanvas) {
      try {
        chartInstances.value[chartIdStress.value] = new Chart(stressCanvas.getContext('2d'), {
          type: chartType.value,
          data: {
            labels: subcategoryLabels.value,
            datasets: buildDatasets('stress_percent')
          },
          options: getChartOptions('stress_percent')
        })
      } catch (e) {
        console.error('Error creating stress chart:', e)
      }
    }
  })
}

const changeChartType = (newType) => {
  chartType.value = newType;
  renderCharts()
}

watch([() => props.surveyReport, () => props.selectedCategory, () => props.selectedPopulation, chartType, subcategoryLabels],
    () => {
      if (isComponentMounted.value) {
        renderCharts()
      }
    },
    { immediate: false, deep: true }
)

onMounted(() => {
  isComponentMounted.value = true
  renderCharts()
})

onUnmounted(() => {
  isComponentMounted.value = false
  destroyCharts()
})
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
}

.btn-icon {
  background: none;
  border: none;
  color: #6c757d;
  padding: 0.25rem 0.5rem;
}

.btn-icon:hover {
  color: #0d6efd;
}
</style>